import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {useDispatch} from 'react-redux';
import {
    IProductBundle,
    IProductBundleItem,
    IProductBundleItemDetails,
    IStorefrontProductBundleGroup,
    IStorefrontProductBundlePricing
} from '../../models/ProductBundles';

import {
    Checkbox, ComboBox, CommandBar, DefaultButton, Dialog, DialogFooter, DialogType,
    IComboBox,
    IComboBoxOption, ICommandBarItemProps, IconButton, Image, MessageBar, MessageBarType,
    PanelType, Pivot, PivotItem,
    PrimaryButton,
    SelectableOptionMenuItemType, Spinner, SpinnerSize, Stack, TextField} from '@fluentui/react';
import {useGetStorefrontsQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {IProductImage} from '../../models/Search';
import {StorefrontBundleImages} from "../Storefront/StorefrontBundleImages";
import {cloneDeep} from "lodash";
import ToggleField from "../Common/ToggleField";
import DataField from "../Common/DataField";
import PanelWrapper from "../Common/PanelWrapper";
import BundleStorefrontEntryDetails from "./BundleStorefrontEntryDetails";
import RichTextField from "../Common/RichTextField";
import StorefrontBundleQuestionsForm from "../Storefront/StorefrontBundleQuestionsForm"; 
import {
    useDeleteProductBundleItemMutation,
    useDeleteProductBundleMutation,
    useGetProductBundleItemsQuery,
    useGetProductBundleQuery, usePatchProductBundleItemMutation,
    usePatchProductBundleMutation
} from "../../store/endpoints/ProductBundleEndpoints";
import DataGrid from "../DataGrid/DataGrid";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";
import BestPriceDisplay from "../Products/BestPriceDisplay";
import ProductBundleItemPriceEntry from "./ProductBundleItemPriceEntry";
import ProductBundleQuantityEntry from "./ProductBundleItemQuantityEntry";
import ProductBundleVendorSelection from "./ProductBundleVendorSelection";
import SavedSearchBundleItemRow from "./SavedSearchBundleItemRow";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import DetailsModal from "../DetailsModal/DetailsModal";
import {calculateBestPrice, checkStock} from "../../logic/Product";
import MarkupTypeSelection from "../Storefront/MarkupTypeSelection";
import {useUrlTabState} from "../../hooks/useUrlState";
import {openGlobalSearch} from '../GlobalSearch/useGlobalSearchHotkey';
import {addProductToDestination} from '../../logic/addToDestination';
import {hasDraggedProducts, IDraggedProduct, readDraggedProducts} from '../../logic/productDrag';
import {IActiveDestination} from '../../models/Destination';

/** Width of the editor when it docks beside the search results. */
export const BUNDLE_DOCK_WIDTH = 940;

interface IStorefrontBundleModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    /**
     * Search mode: the editor sits beside the product grid instead of over it, takes products dropped
     * from the results, offers "Add selected (n) to bundle" (Add Item stays: its suggestions are useful here too).
     */
    docked?: boolean;
    /** The grid's current selection, for the "Add selected" command in docked mode. */
    selectedProducts?: INormalizedProduct[];
    bundleId: string;
}

const StorefrontBundleModal: React.FC<IStorefrontBundleModalProps> = (props) => {
    // Bound to ?panelTab= (not left uncontrolled) so a link can select the Questions tab directly:
    // BundleModalHost already makes the modal itself addressable via ?panel=bundle&instance=<id>,
    // but without this the Pivot always opened on Details regardless of the URL.
    const [panelTab, setPanelTab] = useUrlTabState('details', {param: 'panelTab'});
    const [bundleName, setBundleName] = React.useState<string>('');
    const [partNumber, setPartNumber] = React.useState<string>();
    const [bundleDescription, setBundleDescription] = useState<string>();
    const [bundleImageUrl, setBundleImageUrl] = React.useState<string>('');
    const [selectedStorefronts, setSelectedStorefronts] = React.useState<string[]>([]);
    const [pricing, setPricing] = React.useState<IStorefrontProductBundlePricing[]>([]);
    const [productImages, setProductImages] = React.useState<IProductImage[]>([]);
    const [hideItemDetails, setHideItemDetails] = React.useState<boolean>();
    const [includeItemQuestions, setIncludeItemQuestions] = React.useState<boolean>();
    const [includeInAllStorefronts, setIncludeInAllStorefronts] = React.useState<boolean>();
    const [alwaysAvailable, setAlwaysAvailable] = React.useState<boolean>();
    const [minimumPrice, setMinimumPrice] = React.useState<number | undefined>();
    const [descriptionSourceItems, setDescriptionSourceItems] = React.useState<string[]>([]);
    const [productGroups, setProductGroups] = React.useState<IStorefrontProductBundleGroup[]>([]);
    const storefronts = useGetStorefrontsQuery();
    const [saveBundle] = usePatchProductBundleMutation();
    const [saveBundleItem] = usePatchProductBundleItemMutation();
    const [products, setProducts] = React.useState<IProductBundleItem[]>([]);
    const [detailedProductId, setDetailsProductId] = React.useState<string>();
    const [bundle, setBundle] = React.useState<IProductBundle | undefined>();
    const [catalog, setCatalog] = React.useState<string>();
    const [showConfirmDelete, setShowConfirmDelete] = React.useState<boolean>(false);
    const [showConfirmDeleteItem, setShowConfirmDeleteItem] = React.useState<boolean>(false);
    const [bundleItemToDelete, setBundleItemToDelete] = React.useState<string>();
    const [saveStatus, setSaveStatus] = React.useState<string | undefined>();
    const [saveStatusType, setSaveStatusType] = React.useState<MessageBarType>(MessageBarType.info);
    

    const dispatch = useDispatch();
    const bundleFetch = useGetProductBundleQuery(props.bundleId, {skip: !props.bundleId || props.bundleId === ''});
    const bundleItems = useGetProductBundleItemsQuery(props.bundleId ?? '', {skip: !props.bundleId || props.bundleId === ''});
    const [bundleDelete] = useDeleteProductBundleMutation();
    const [bundleItemDelete] = useDeleteProductBundleItemMutation();
    
    const [bundleLoading, setBundleLoading] = useState<boolean>(false);

    useEffect(() => {
        return () => {
            setSaveStatus(undefined)
        }
    }, []);
    
    useEffect(() => {
        if(bundleItems.data?.items){
            // Order by sortOrder so drag-and-drop reordering (persisted as sortOrder per item) survives
            // the optimistic cache update, which replaces items in place without reordering the cache.
            setProducts([...bundleItems.data.items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
        }
    }, [bundleItems.data]);

    useEffect(() => {
        if(bundleFetch.data){
            setBundle(bundleFetch.data);
        }
    }, [bundleFetch.data]);

    // Hide the save message after a few seconds if successful
    useEffect(() => {
        if (saveStatusType === MessageBarType.success && saveStatus) {
            const timeout = setTimeout(() => {
                setSaveStatus("");
            }, 2500);
            return () => clearTimeout(timeout);
        }
    }, [saveStatus, saveStatusType]);
    
    useEffect(() => {
        setBundleLoading(true);
        if (bundle == null) return;
        setBundleName(bundle?.name ?? '');
        setBundleImageUrl(bundle?.imageUrl ?? '');
        setSelectedStorefronts(bundle.details?.storefronts ?? []);
        setPricing(bundle.details?.pricing ?? []);
        console.log('settings hide item details:', bundle.details?.hideLineItemDetails)
        setHideItemDetails(bundle.details?.hideLineItemDetails === true);
        setDescriptionSourceItems(bundle.details?.descriptionSourceItems?? []);
        setIncludeInAllStorefronts(bundle.details?.includeInAllStorefronts ?? false);
        setAlwaysAvailable(bundle.details?.alwaysAvailable ?? false);
        setMinimumPrice(bundle.details?.minimumPrice);
        setIncludeItemQuestions(bundle.details?.includeProductQuestions ?? false)
        setPartNumber(bundle?.partNumber)
        setProductGroups(bundle.details?.productGroups ?? [])
        setBundleLoading(false)
    }, [bundle?.details]);
    
    React.useEffect(() => {
        if (bundle == undefined) return;
        setBundleDescription(bundle?.description);
    }, [bundle?.description]);

    const assignItemToGroup = (item: IProductBundleItem, groupId: string) => {
        const targetGroup = productGroups.find(g => g.id === groupId);
        const groupHasDefault = targetGroup?.products.some(pid =>
            products.find(p => p.productId === pid)?.details?.isDefault === true
        ) ?? false;
        const shouldBeDefault = !groupHasDefault;

        setProductGroups(productGroups.map(group => {
            if (group.id === groupId) {
                return group.products.includes(item.productId)
                    ? group
                    : {...group, products: [...group.products, item.productId]};
            }
            return {...group, products: group.products.filter(id => id !== item.productId)};
        }));

        const updatedItems = products.map(p => {
            if (p.productId !== item.productId) return p;
            const newItem = {...p, details: {...p.details, optionality: groupId, isDefault: shouldBeDefault || p.details?.isDefault}};
            if (newItem.id) {
                saveBundleItem({
                    bundleId: props.bundleId,
                    productId: newItem.id,
                    request: newItem
                }).unwrap().then((result) => {
                    if (result.success) {
                        setSaveStatus("Item saved successfully");
                        setSaveStatusType(MessageBarType.success);
                    } else {
                        setSaveStatus("Failed to save bundle item");
                        setSaveStatusType(MessageBarType.error);
                    }
                }).catch(() => {
                    setSaveStatus("Failed to save bundle item");
                    setSaveStatusType(MessageBarType.error);
                });
            }
            return newItem;
        });
        setProducts(updatedItems);
    }

    const updateItemOptionality = (item: IProductBundleItem, optionality: string) => {
        const updatedItems = products.map(p => {
            if (p.productId !== item.productId) return p;
            const newItem = {...p, details: {...p.details, optionality}};
            if (newItem.id) {
                saveBundleItem({
                    bundleId: props.bundleId,
                    productId: newItem.id,
                    request: newItem
                }).unwrap().then((result) => {
                    if (result.success) {
                        setSaveStatus("Item saved successfully");
                        setSaveStatusType(MessageBarType.success);
                    } else {
                        setSaveStatus("Failed to save bundle item");
                        setSaveStatusType(MessageBarType.error);
                    }
                }).catch(() => {
                    setSaveStatus("Failed to save bundle item");
                    setSaveStatusType(MessageBarType.error);
                });
            }
            return newItem;
        });
        setProducts(updatedItems);
    }

    const removeItemFromGroup = (item: IProductBundleItem) => {
        const currentGroup = productGroups.find(g => g.products.includes(item.productId));
        // If the removed item was the group default, promote the first remaining member to default.
        const wasDefault = item.details?.isDefault === true;
        const newDefaultProductId = wasDefault
            ? currentGroup?.products.find(pid => pid !== item.productId)
            : undefined;

        setProductGroups(productGroups.map(group => ({
            ...group,
            products: group.products.filter(id => id !== item.productId),
        })));

        const updatedItems = products.map(p => {
            if (p.productId === item.productId) {
                const newItem = {...p, details: {...p.details, optionality: 'Required', isDefault: false, changeQuantity: false}};
                persistBundleItem(newItem);
                return newItem;
            }
            if (newDefaultProductId && p.productId === newDefaultProductId) {
                const newItem = {...p, details: {...p.details, isDefault: true}};
                persistBundleItem(newItem);
                return newItem;
            }
            return p;
        });
        setProducts(updatedItems);
    }

    const persistBundleItem = (newItem: IProductBundleItem) => {
        if (!newItem.id) return;
        saveBundleItem({
            bundleId: props.bundleId,
            productId: newItem.id,
            request: newItem
        }).unwrap().then((result) => {
            if (result.success) {
                setSaveStatus("Item saved successfully");
                setSaveStatusType(MessageBarType.success);
            } else {
                setSaveStatus("Failed to save bundle item");
                setSaveStatusType(MessageBarType.error);
            }
        }).catch(() => {
            setSaveStatus("Failed to save bundle item");
            setSaveStatusType(MessageBarType.error);
        });
    }

    const updateItemDefault = (item: IProductBundleItem, group: IStorefrontProductBundleGroup) => {
        const updatedItems = products.map(p => {
            if (!group.products.includes(p.productId)) return p;
            const shouldBeDefault = p.productId === item.productId;
            if ((p.details?.isDefault === true) === shouldBeDefault) return p;
            const newItem = {...p, details: {...p.details, isDefault: shouldBeDefault}};
            if (newItem.id) {
                saveBundleItem({
                    bundleId: props.bundleId,
                    productId: newItem.id,
                    request: newItem
                }).unwrap().then((result) => {
                    if (result.success) {
                        setSaveStatus("Item saved successfully");
                        setSaveStatusType(MessageBarType.success);
                    } else {
                        setSaveStatus("Failed to save bundle item");
                        setSaveStatusType(MessageBarType.error);
                    }
                }).catch(() => {
                    setSaveStatus("Failed to save bundle item");
                    setSaveStatusType(MessageBarType.error);
                });
            }
            return newItem;
        });
        setProducts(updatedItems);
    }

    const updateBundleItem = (itemId: string, updatedDetails: Partial<IProductBundleItemDetails>) => {

        const updatedItems = products.map(i => {
            if(i.productId === itemId){
                const newItem = {...i, details: {...i.details, ...updatedDetails}}
                if(newItem.id) {
                    saveBundleItem({
                        bundleId: props.bundleId,
                        productId: newItem.id,
                        request: newItem
                    }).unwrap().then((result) => {
                        if (result.success) {
                            setSaveStatus("Item saved successfully");
                            setSaveStatusType(MessageBarType.success);
                        } else {
                            setSaveStatus("Failed to save bundle item");
                            setSaveStatusType(MessageBarType.error);
                        }
                    }).catch(() => {
                        setSaveStatus("Failed to save bundle item");
                        setSaveStatusType(MessageBarType.error);
                    });
                }
                return newItem;
            }
            return i;
        });
        setProducts(updatedItems);
    }

    // The saved-search item is keyed by its item id rather than a productId (it has none), so its
    // quantity edits patch by id directly instead of going through updateBundleItem.
    const updateSavedSearchItemQuantity = (item: IProductBundleItem, quantity: number) => {
        if (!item.id) return;
        const updated = {...item, details: {...item.details, quantity}};
        setProducts(products.map(p => p.id === item.id ? updated : p));
        saveBundleItem({bundleId: props.bundleId, productId: item.id, request: updated})
            .unwrap().then((result) => {
                setSaveStatus(result.success ? "Item saved successfully" : "Failed to save bundle item");
                setSaveStatusType(result.success ? MessageBarType.success : MessageBarType.error);
            }).catch(() => {
                setSaveStatus("Failed to save bundle item");
                setSaveStatusType(MessageBarType.error);
            });
    };

    const saveReorderedItems = (reordered: IProductBundleItem[]) => {
        reordered.forEach((item, idx) => {
            if (!item.id) return;
            const updated = {...item, sortOrder: idx};
            saveBundleItem({
                bundleId: props.bundleId,
                productId: item.id,
                request: updated,
            }).unwrap().then((result) => {
                if (result.success) {
                    setSaveStatus('Item saved successfully');
                    setSaveStatusType(MessageBarType.success);
                } else {
                    setSaveStatus('Failed to save bundle item');
                    setSaveStatusType(MessageBarType.error);
                }
            }).catch(() => {
                setSaveStatus('Failed to save bundle item');
                setSaveStatusType(MessageBarType.error);
            });
            setProducts(prev => prev.map(p => p.productId === item.productId ? updated : p));
        });
    };

    useEffect(() => {
        if(products) {
            const images: IProductImage[] = [];
            products?.forEach(i => {
                if(i.sourceItem?.imageUrl) {
                    const productImage: IProductImage = {
                        name: "Product Image",
                        url: i.sourceItem.imageUrl,
                        type: "Product",
                        id: i.productId,
                        isCustom: true
                    };
                    images.push(productImage);
                }
            })

            setProductImages([...images]);
        }
    }, [products]);
    
    const save = async () => {
        // Save the storefront bundle
        if (!bundle?.id) return;
        await saveBundle({
            id: bundle.id, 
            request: {
                partNumber: partNumber,
                name: bundleName,
                description: bundleDescription,
                imageUrl: bundleImageUrl,
                details: {
                    storefronts: selectedStorefronts,
                    pricing: pricing,
                    descriptionSourceItems: descriptionSourceItems,
                    hideLineItemDetails: hideItemDetails,
                    includeProductQuestions: includeItemQuestions,
                    includeInAllStorefronts: includeInAllStorefronts,
                    // A saved-search bundle can never be always available (the toggle is disabled above).
                    alwaysAvailable: hasSavedSearch ? false : alwaysAvailable,
                    // An explicit zero clears the floor on the API side; omitting the field would leave it stale.
                    minimumPrice: minimumPrice ?? 0,
                    productGroups: productGroups,
                }}
        });
        props.onDismiss();
    }

    const normalizeProduct = (item: IProductBundleItem) => {
        const normalizedProduct: INormalizedProduct = {
            id: item.productId,
            catalog: item.catalog || '',
            name: item.sourceItem?.name || '',
            image: item.sourceItem?.imageUrl || '',
            vendors: item?.vendors || [],
            images: [],
            links: [],
            attributes: [],
            facets: [],
            filters: []
        }

        if (normalizedProduct.vendors?.some(checkStock)) {
            normalizedProduct.stock = true;
        }

        let [bestPrice, bestBasePrice, bestVendor, onHand] = calculateBestPrice(normalizedProduct, item.details?.quantity ?? 1, true, true);
        if (bestPrice == null) [bestPrice, bestBasePrice, bestVendor, onHand] = calculateBestPrice(normalizedProduct, item.details?.quantity ?? 1, true, false);
        if (bestPrice == null) [bestPrice, bestBasePrice, bestVendor, onHand] = calculateBestPrice(normalizedProduct, item.details?.quantity ?? 1,false, false);
        normalizedProduct.bestPrice = bestPrice;
        normalizedProduct.bestBasePrice = bestBasePrice;
        normalizedProduct.bestVendor = bestVendor;
        normalizedProduct.bestVendorOnHand = onHand;
        return normalizedProduct;
    }
    
    // A saved-search bundle cannot be "always available": its members are resolved live against
    // storefront availability, so there is no fixed set of products to force-include.
    const hasSavedSearch = products.some(p => p.savedSearchId);

    const options = products?.map((product) => ({key: product?.productId, text: product?.sourceItem?.name?.substring(0,50) ?? ""})) ?? []

    const onChange = (
        event: React.FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string,
    ): void => {

        const selected = option?.selected;
        const currentSelectedOptionKeys = descriptionSourceItems.filter(key => key !== 'selectAll');
        const selectAllState = currentSelectedOptionKeys.length === options.length;

        if (option) {
            if (option?.itemType === SelectableOptionMenuItemType.SelectAll) {
                selectAllState
                    ? setDescriptionSourceItems([])
                    : setDescriptionSourceItems(['selectAll', ...options.map(o => o.key as string)]);
            } else {
                const updatedKeys = selected
                    ? [...currentSelectedOptionKeys, option?.key as string]
                    : currentSelectedOptionKeys.filter(k => k !== option.key);
                if (updatedKeys.length === options.length) {
                    updatedKeys.push('selectAll');
                }
                setDescriptionSourceItems(updatedKeys);
            }
        }
        
    };
    
    const footerButtons = () =>[
        <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
            <PrimaryButton
                text='Save'
                onClick={save}
                style={{width:'90%', marginRight: '10px'}}
            />
            <DefaultButton
                text='Cancel'
                onClick={props.onDismiss}
                style={{width:'90%'}}
            />
        </div>
    ]

    const getCustomColumns = (withOptionality = false, group?: IStorefrontProductBundleGroup) => {
        const columnDetails: IColumnDetails[] = [
            {
                index: 0,
                identifier: 'Details',
                label: '',
                type: '',
                effectiveWidth: 40,
                component:(q: IProductBundleItem) => {
                    const currentGroup = productGroups.find(g => g.products.includes(q.productId));
                    const menuItems = [
                        ...productGroups
                            .filter(g => g.id !== currentGroup?.id)
                            .map(grp => ({
                                key: grp.id ?? '',
                                text: grp.name ?? 'Unnamed Group',
                                onClick: () => assignItemToGroup(q, grp.id ?? ''),
                            })),
                        ...(currentGroup ? [{
                            key: 'remove',
                            text: 'Remove from group',
                            iconProps: {iconName: 'Cancel'},
                            onClick: () => removeItemFromGroup(q),
                        }] : []),
                    ];
                    return (
                        <div style={{display: 'flex', flexDirection: 'column'}}>
                            <IconButton iconProps={{iconName: 'edit'}} onClick={() => {
                                setDetailsProductId(q.productId)
                                setCatalog(q.catalog || '')
                            }}/>
                            <IconButton
                                iconProps={{iconName: 'MoveToFolder'}}
                                title='Assign to group'
                                disabled={menuItems.length === 0}
                                menuProps={menuItems.length > 0 ? {items: menuItems} : undefined}
                            />
                            <IconButton iconProps={{iconName: 'delete'}} onClick={() => {
                                setBundleItemToDelete(q.id)
                                setShowConfirmDeleteItem(true)
                            }}/>
                        </div>
                    );
                },
                required: true,
            },
            {
                identifier: 'Thumbnail',
                label: '',
                type: '',
                effectiveWidth: 40,
                component:(q: IProductBundleItem) =>
                    <img src={q?.sourceItem?.imageUrl} alt={q?.sourceItem?.imageUrl} style={{ maxHeight: '40px', maxWidth: '40px', margin: '0 auto' }}/>,
                required: true,
            },
            {
                identifier: 'name',
                label: 'Name',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IProductBundleItem) => <div>{q.sourceItem?.name}</div>,

            },
            {
                identifier: 'bestPrice',
                label: 'Best Price',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                effectiveWidth: 120,
                minimumWidth: 120,
                component:(p: IProductBundleItem) =>
                    <BestPriceDisplay product={normalizeProduct(p)} />,
            },
            {
                identifier: 'price',
                label: 'Override Price',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                effectiveWidth: 100,
                minimumWidth: 100,
                component:(p: IProductBundleItem) =>
                    <ProductBundleItemPriceEntry item={p} onChange={(v) => updateBundleItem(p.productId, {...p.details, price: v}) }/>,

            },
            {
                identifier: 'quantity',
                label: 'Quantity',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                effectiveWidth: 110,
                minimumWidth: 110,
                component:(i: IProductBundleItem) => (
                    <div className='bundle-quantity-cell'>
                        <ProductBundleQuantityEntry
                            item={i}
                            onChange={(v) => updateBundleItem(i.productId, { quantity: v })}
                        />
                        {withOptionality && (
                            // Hangs below the input rather than stacking with it, so the input keeps the
                            // row's center line and lines up with Override Price and Source. See App.css.
                            <div className='bundle-quantity-editable'>
                                <ToggleField
                                    inline={true}
                                    label='Editable'
                                    checked={i.details?.changeQuantity === true}
                                    onChange={(_, checked) => updateBundleItem(i.productId, { changeQuantity: checked ?? false })}
                                />
                            </div>
                        )}
                    </div>
                ),
            },
            {
                identifier: 'source',
                label: 'Source',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                effectiveWidth: 150,
                minimumWidth: 150,
                component:(i: IProductBundleItem) => <ProductBundleVendorSelection
                    item={i}
                    onSelectedItemChanged={(v) => updateBundleItem(i.productId, {...v.details}) }
                />,
            },
            ...(withOptionality ? [
                {
                    identifier: 'Optional',
                    label: 'Optional',
                    type: '',
                    effectiveWidth: 60,
                    required: true,
                    component: (q: IProductBundleItem) => (
                        <Checkbox
                            checked={q.details?.optionality === 'Optional'}
                            onChange={(_, checked) => updateItemOptionality(q, checked ? 'Optional' : 'Required')}
                        />
                    ),
                },
            ] : []),
            ...(group ? [
                {
                    identifier: 'Default',
                    label: 'Default',
                    type: '',
                    effectiveWidth: 60,
                    required: true,
                    component: (q: IProductBundleItem) => (
                        <input
                            type='radio'
                            name={`default-${group.id}`}
                            checked={q.details?.isDefault === true}
                            onChange={() => updateItemDefault(q, group)}
                        />
                    ),
                },
            ] : []),
        ];
        return columnDetails
    }
    const commands: ICommandBarItemProps[] = [
        {
            key: 'delete',
            text: 'Delete Bundle',
            iconProps: { iconName: 'Delete' },
            onClick: () => {setShowConfirmDelete(true)}
        }
    ];

    // ---- products arriving from the search results (a drop or the Add selected command) ----
    const [dragOver, setDragOver] = useState(false);
    const [addingCount, setAddingCount] = useState(0);
    const addProducts = async (refs: IDraggedProduct[]) => {
        const wanted = refs.filter((r) => r.id && r.catalog);
        if (wanted.length === 0 || !props.bundleId) return;
        setAddingCount(wanted.length);
        const destination: IActiveDestination = {type: 'bundle', id: props.bundleId, name: bundle?.name};
        const toastId = toast.loading(`Adding ${wanted.length === 1 ? (wanted[0].name ?? 'product') : `${wanted.length} products`} to ${bundle?.name ?? 'the bundle'}…`);
        let added = 0;
        for (const ref of wanted) {
            try {
                if (await addProductToDestination(dispatch, destination, {id: ref.id, catalog: ref.catalog} as INormalizedProduct)) added += 1;
            } catch { /* counted below */ }
        }
        setAddingCount(0);
        const failed = wanted.length - added;
        toast.update(toastId, {
            type: failed === 0 ? 'success' : added === 0 ? 'error' : 'warning',
            render: failed === 0
                ? `Added ${added === 1 ? (wanted[0].name ?? 'product') : `${added} products`} to ${bundle?.name ?? 'the bundle'}`
                : `Added ${added} of ${wanted.length} to ${bundle?.name ?? 'the bundle'}`,
            isLoading: false,
            autoClose: 3000,
        });
    };
    const selectedRefs: IDraggedProduct[] = (props.selectedProducts ?? []).map((p) => ({id: p.id, catalog: p.catalog, name: p.name}));

    // The header's own menu bar: the bundle actions (delete), plus, when docked beside the search
    // results with rows selected, adding that selection in one click.
    const headerCommands: ICommandBarItemProps[] = [
        ...(props.docked && selectedRefs.length > 0 ? [{
            key: 'addSelected',
            text: `Add selected (${selectedRefs.length}) to bundle`,
            iconProps: {iconName: 'Add'},
            disabled: addingCount > 0,
            onClick: () => { void addProducts(selectedRefs); },
        } as ICommandBarItemProps] : []),
        ...commands,
    ];
    const header = () => {
        return(
            <div style={{width: '100%', minWidth: 0}}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <Image src={bundle?.imageUrl} style={{maxWidth: '50px', paddingLeft: '24px'}}/>
                    <div style={{paddingLeft: '24px', whiteSpace: 'nowrap'}}><h1>Edit Bundle</h1></div>
                </div>
                {/* Its own row: beside the title the bar had no room and folded into an ellipsis. */}
                <CommandBar
                    items={headerCommands}
                    styles={{root: {backgroundColor: 'transparent', paddingLeft: 16, height: 36}}}
                    overflowButtonProps={{ariaLabel: 'More actions'}}
                />
            </div>
        )
    }
    return (
        <div>
            <Dialog
                hidden={!showConfirmDelete}
                onDismiss={() => setShowConfirmDelete(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Delete Product Bundle',
                    subText: 'Are you sure you want to delete this bundle?',
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Delete'
                        onClick={() => {
                            bundleDelete({id: props.bundleId})
                            setShowConfirmDelete(false)
                            props.onDismiss()
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => setShowConfirmDelete(false)}
                    />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={!showConfirmDeleteItem}
                onDismiss={() => setShowConfirmDeleteItem(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Delete Product Bundle Item',
                    subText: 'Are you sure you want to delete this item?',
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Delete'
                        onClick={() => {
                            if(bundleItemToDelete) {
                                bundleItemDelete({
                                    bundleId: props.bundleId,
                                    productBundleItem: bundleItemToDelete
                                })
                                setShowConfirmDeleteItem(false)
                                setBundleItemToDelete(undefined)
                            }
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => setShowConfirmDelete(false)}
                    />
                </DialogFooter>
            </Dialog>
            <DetailsModal 
                product={detailedProductId ?? ''} 
                catalog={catalog ?? ''} 
                open={detailedProductId !== undefined} 
                onClose={() => setDetailsProductId(undefined)} 
            />
            <PanelWrapper
                isOpen={props.isOpen}
                onDismiss={props.onDismiss}
                onRenderHeader={header}
                title='Bundle'
                type={PanelType.custom}
                customWidthPx={props.docked ? BUNDLE_DOCK_WIDTH : 1200}
                // Docked: beside the results like the search criteria panel, no overlay, grid interactive.
                isBlocking={props.docked ? false : undefined}
                layerProps={props.docked ? {className: 'search-panel'} : undefined}
                footerButtons={footerButtons()}
                //onRenderFooter={footer}
                isFooterAtBottom={true}
                styles={{
                    root: {
                        zIndex: 10,
                        marginTop: 'var(--app-header-offset)',
                    }
                }}
            >
                <div
                    // The whole editor is the drop target for rows dragged out of the results.
                    onDragOver={props.docked ? (e) => { if (hasDraggedProducts(e.dataTransfer)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!dragOver) setDragOver(true); } } : undefined}
                    onDragLeave={props.docked ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false); } : undefined}
                    onDrop={props.docked ? (e) => { if (!hasDraggedProducts(e.dataTransfer)) return; e.preventDefault(); setDragOver(false); void addProducts(readDraggedProducts(e.dataTransfer)); } : undefined}
                    style={props.docked ? {
                        minHeight: '100%',
                        outline: dragOver ? '3px dashed #789c2f' : '3px dashed transparent',
                        outlineOffset: -6,
                        borderRadius: 6,
                        background: dragOver ? 'rgba(120, 156, 47, 0.06)' : undefined,
                        transition: 'background 0.15s ease, outline-color 0.15s ease',
                    } : undefined}
                >
                    {props.docked && (
                        <MessageBar messageBarType={dragOver ? MessageBarType.success : MessageBarType.info} isMultiline={false} styles={{root: {marginBottom: 8}}}>
                            {dragOver
                                ? 'Drop to add to this bundle'
                                : 'Drag products from the results onto this panel, or select rows and use "Add selected" above.'}
                        </MessageBar>
                    )}
                    <Pivot
                        overflowBehavior='menu'
                        styles={{itemContainer: {marginTop: '1em'}}}
                        selectedKey={panelTab}
                        onLinkClick={(item) => setPanelTab(item?.props.itemKey)}
                    >

                        <PivotItem itemKey='details' headerText='Details'>
                            {bundleLoading ? <Spinner label={'Loading Bundle Details'} size={SpinnerSize.large}/> : 
                            <Stack>
                                <DataField
                                    toolTip={'This defines the name of the bundle as it will appear in the storefront'}
                                    label='Name'
                                    value={bundleName}
                                    onChange={(e, newValue) => setBundleName(newValue ?? '')}
                                />
                                <DataField
                                    label='Bundle Part Number'
                                    value={partNumber}
                                    onChange={(e, newValue) => setPartNumber(newValue ?? '')}
                                />
                                <div style={{paddingTop: '1em'}}>
                                    <RichTextField
                                        label='Description'
                                        value={bundleDescription}
                                        onChange={(value) => {
                                            setBundleDescription(value);
                                        }}
                                    />
                                </div>
                                <ComboBox
                                    label='Description Source Items'
                                    selectedKey={descriptionSourceItems}
                                    onChange={onChange}
                                    options={options}
                                    multiSelect={true}
                                />
                                <ToggleField
                                    inline={true}
                                    label={'Hide Item Details'}
                                    toolTip={'When checked, bundle item descriptions, quantities, and images will not be displayed in the storefront'}
                                    checked={hideItemDetails}
                                    onChange={(e, v) => setHideItemDetails(v)}
                                />
                                </Stack>
                            }
                        </PivotItem>
                        <PivotItem itemKey='items' headerText={'Items'}>
                            {saveStatus ? (
                                <MessageBar messageBarType={saveStatusType} isMultiline={false} styles={{root: {marginLeft: 8, minWidth: 200}}}>
                                    {saveStatus}
                                </MessageBar>
                            ) : <div style={{height: '28px'}}></div>}
                            {products.filter(p => p.savedSearchId).map(savedSearchItem => (
                                <SavedSearchBundleItemRow
                                    key={savedSearchItem.id}
                                    item={savedSearchItem}
                                    onQuantityChange={(q) => updateSavedSearchItemQuantity(savedSearchItem, q)}
                                    onRemove={() => {
                                        setBundleItemToDelete(savedSearchItem.id);
                                        setShowConfirmDeleteItem(true);
                                    }}
                                />
                            ))}
                            <div style={{marginBottom: '12px', display: 'flex', gap: '0.5em'}}>
                                <PrimaryButton
                                    text='Add Item'
                                    iconProps={{iconName: 'Search'}}
                                    title='Search products and add them to this bundle without leaving the search'
                                    onClick={() => openGlobalSearch({mode: 'products', context: {kind: 'bundle', bundleId: props.bundleId, name: bundle?.name}})}
                                />
                                <DefaultButton
                                    text='Create Group'
                                    iconProps={{iconName: 'Add'}}
                                    onClick={() =>
                                        setProductGroups([
                                            ...productGroups,
                                            {id: `group-${Date.now()}`, name: 'New Group', products: []},
                                        ])
                                    }
                                />
                            </div>
                            {productGroups.map((group, index) => (
                                <div
                                    key={group.id}
                                    style={{
                                        marginBottom: '16px',
                                        border: '1px solid #edebe9',
                                        borderRadius: '4px',
                                        padding: '12px',
                                    }}
                                >
                                    <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px'}}>
                                        <TextField
                                            value={group.name ?? ''}
                                            placeholder='Group name'
                                            onChange={(_, v) =>
                                                setProductGroups(productGroups.map((g, i) =>
                                                    i === index ? {...g, name: v ?? ''} : g
                                                ))
                                            }
                                            styles={{root: {flex: 1}}}
                                        />
                                        <IconButton
                                            iconProps={{iconName: 'ChevronUp'}}
                                            title='Move group up'
                                            disabled={index === 0}
                                            onClick={() => {
                                                const next = [...productGroups];
                                                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                                setProductGroups(next);
                                            }}
                                        />
                                        <IconButton
                                            iconProps={{iconName: 'ChevronDown'}}
                                            title='Move group down'
                                            disabled={index === productGroups.length - 1}
                                            onClick={() => {
                                                const next = [...productGroups];
                                                [next[index], next[index + 1]] = [next[index + 1], next[index]];
                                                setProductGroups(next);
                                            }}
                                        />
                                        <IconButton
                                            iconProps={{iconName: 'Delete'}}
                                            title='Delete group'
                                            onClick={() =>
                                                setProductGroups(productGroups.filter((_, i) => i !== index))
                                            }
                                        />
                                    </div>
                                    <DataGrid
                                        className='bundle-items-grid'
                                        getItemId={(item) => item.productId}
                                        items={(group.products ?? [])
                                            .map(pid => products.find(p => p.productId === pid))
                                            .filter((p): p is IProductBundleItem => !!p)}
                                        loading={bundleItems.isLoading || bundleItems.isFetching}
                                        overrideColumns={getCustomColumns(false, group)}
                                        selectionMode={GridSelectionMode.none}
                                        useContentHeight={true}
                                        draggableRows={true}
                                        onRowReorder={(from, to) => {
                                            const next = [...(group.products ?? [])];
                                            const [moved] = next.splice(from, 1);
                                            next.splice(to, 0, moved);
                                            setProductGroups(productGroups.map(g =>
                                                g.id === group.id ? {...g, products: next} : g
                                            ));
                                            const reordered = next
                                                .map(pid => products.find(p => p.productId === pid))
                                                .filter((p): p is IProductBundleItem => !!p);
                                            saveReorderedItems(reordered);
                                        }}
                                    />
                                </div>
                            ))}
                            <DataGrid
                                className='bundle-items-grid'
                                getItemId={(item) => item.productId}
                                items={products.filter(p => !p.savedSearchId && !productGroups.some(g => g.products.includes(p.productId)))}
                                loading={bundleItems.isLoading || bundleItems.isFetching}
                                overrideColumns={getCustomColumns(true)}
                                selectionMode={GridSelectionMode.none}
                                draggableRows={true}
                                onRowReorder={(from, to) => {
                                    // The saved-search item is pinned above groups and never participates in
                                    // reordering, so exclude it from the reorderable ungrouped set.
                                    const isReorderable = (p: IProductBundleItem) => !p.savedSearchId && !productGroups.some(g => g.products.includes(p.productId));
                                    const ungrouped = products.filter(isReorderable);
                                    const [moved] = ungrouped.splice(from, 1);
                                    ungrouped.splice(to, 0, moved);
                                    setProducts([...products.filter(p => !isReorderable(p)), ...ungrouped]);
                                    saveReorderedItems(ungrouped);
                                }}
                            />
                        </PivotItem>
                        <PivotItem itemKey='storefronts' headerText='Storefronts'>
                            <ToggleField
                                inline={true}
                                label={'Include in All Storefronts'}
                                toolTip={'When checked, bundle will be included in all storefronts by default. Uncheck to select specific storefronts.'}
                                checked={includeInAllStorefronts}
                                onChange={(e, v) => setIncludeInAllStorefronts(v)}
                            />
                            <ToggleField
                                inline={true}
                                label={'Always Available'}
                                disabled={hasSavedSearch}
                                checked={alwaysAvailable && !hasSavedSearch}
                                toolTip={<div>This forces Adaptive Catalog to include this bundle in the storefront, even if it is not otherwise considered in stock.<br></br><br></br> <b>When enabling this option, it is highly recommended that you set a minimum price, as out of stock items may not include a cost to mark up or may have out of date costs. </b><br></br><br></br><b>This option is not available for bundles that contain a saved search, since the saved search's results are resolved live against storefront availability.</b></div>}
                                onChange={(e, v) => setAlwaysAvailable(v)}
                            />
                            <div style={{marginBottom: '1em'}}>
                                <TextField
                                    label='Minimum Price'
                                    type='number'
                                    value={minimumPrice == null ? '' : String(minimumPrice)}
                                    styles={{root: {width: 160}}}
                                    onChange={(_, v) => {
                                        if (!v || v.trim() === '') {
                                            setMinimumPrice(undefined);
                                            return;
                                        }
                                        const parsed = parseFloat(v);
                                        if (isNaN(parsed)) {
                                            setMinimumPrice(undefined);
                                            return;
                                        }
                                        setMinimumPrice(Math.max(0, Math.round(parsed * 100) / 100));
                                    }}
                                />
                                <div style={{fontSize: 11, color: '#666', marginTop: 4}}>
                                    Optional floor for the bundle's price in every storefront. A storefront-specific minimum price below overrides it.
                                </div>
                            </div>
                            <div style={{ width: '100%', overflowX: 'auto' }}>
                                <table className='data-table'>
                                    <thead>
                                    <tr>
                                        <th></th>
                                        <th>Storefront</th>
                                        <th>Included</th>
                                        <th>Markup</th>
                                        <th>Minimum Price</th>
                                        <th>Markup Type</th>
                                        <th>Estimated Price</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {storefronts.data?.items?.map((storefront) => {
                                        const foundIndex = pricing.findIndex(pr => pr.storefront === storefront.id);
                                        return(
                                            <BundleStorefrontEntryDetails
                                                markup={foundIndex >= 0 ? pricing[foundIndex].markup : undefined}
                                                minimumPrice={foundIndex >= 0 ? pricing[foundIndex].minimumPrice : undefined}
                                                bundleId={bundle?.id ?? ''}
                                                includeAllStorefronts={includeInAllStorefronts}
                                                key={storefront.id}
                                                storefrontId={storefront.id}
                                                storefrontName={storefront.name}
                                                included={storefront?.id != undefined && selectedStorefronts.includes(storefront.id)}
                                                items={products}
                                                markupType={foundIndex >= 0 ? pricing[foundIndex].markupType : undefined}
                                                onIncludedChange={(included) => {
                                                    if (included) {
                                                        setSelectedStorefronts(() => [...selectedStorefronts, storefront.id ?? '']);
                                                    } else {
                                                        const filtered = selectedStorefronts.filter((id) => id !== storefront.id) ?? []
                                                        setSelectedStorefronts(() => [...filtered]);
                                                    }
                                                }}
                                                onMinimumPriceChange={(minimumPrice) => {
                                                    if (foundIndex >= 0) {
                                                        const updatedPricing = cloneDeep(pricing)
                                                        updatedPricing[foundIndex].minimumPrice = minimumPrice;
                                                        setPricing(() =>[...updatedPricing]);
                                                    }
                                                    else{
                                                        setPricing(() => [...pricing, {storefront: storefront.id?? '', minimumPrice}]);
                                                    }
                                                }}
                                                onMarkupChange={(markup) => {
                                                    if (foundIndex >= 0) {
                                                        const updatedPricing = cloneDeep(pricing)
                                                        updatedPricing[foundIndex].markup = markup;
                                                        setPricing(() =>[...updatedPricing]);
                                                    }
                                                    else{
                                                        setPricing(() => [...pricing, {storefront: storefront.id?? '', markup}]);
                                                    }
                                                }}
                                                onMarkupTypeChange={(markupType) => {
                                                    if (foundIndex >= 0) {
                                                        const updatedPricing = cloneDeep(pricing)
                                                        updatedPricing[foundIndex].markupType = markupType;
                                                        setPricing(() => [...updatedPricing]);
                                                    }
                                                    else{
                                                        setPricing(() => [...pricing, {storefront: storefront.id?? '', markupType}]);
                                                    }
                                                }}
                                            />
                                        )})}
                                    </tbody>
                                </table>
                            </div>
                        </PivotItem>
                        <PivotItem itemKey='images' headerText='Images'>
                            <StorefrontBundleImages 
                                images={productImages}
                                selected={bundleImageUrl}
                                onSelect={(img) => setBundleImageUrl(img)}
                            />
                        </PivotItem>
                        <PivotItem itemKey='questions' headerText={'Questions'}>
                            {bundle?.id && 
                            <StorefrontBundleQuestionsForm 
                                includeProductQuestions={includeItemQuestions} 
                                onToggleIncludeProductQuestions={(v) => setIncludeItemQuestions(v) } 
                                bundleId={bundle.id} 
                            />}
                        </PivotItem>
                    </Pivot>
                    
                </div>
            </PanelWrapper>
        </div>
    );
}

export default StorefrontBundleModal;